import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  InputNumber,
  Modal, 
  Table, 
  Tag, 
  Space, 
  Typography,
  message,
  Tooltip,
  Dropdown
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined, 
  ReloadOutlined,
  ArrowLeftOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  MoreOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  MinusCircleOutlined,
  FilterOutlined
} from '@ant-design/icons';
import Cron from 'react-js-cron';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';
import PropertyKeyAutoComplete from './PropertyKeyAutoComplete';
import './analysis.css';

const { Title, Text } = Typography;

const SchedulerSettingsPage = () => {
  // Check authentication before rendering
  useAuthCheck();
  
  const navigate = useNavigate();
  
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [cronValue, setCronValue] = useState(null);
  const [form] = Form.useForm();

  const loadScheduledJobs = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await axios.get('/analyze/scheduler/jobs');
      setScheduledJobs(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        message.error('Access denied. Admin privileges required.');
        navigate('/analysis?view=jobs');
      } else if (error.response?.status === 404) {
        setScheduledJobs([]);
      } else {
        message.error('Failed to load scheduled jobs');
        console.error('Error loading scheduled jobs:', error);
      }
    } finally {
      setTableLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadScheduledJobs();
  }, [loadScheduledJobs]);

  const openCreateModal = () => {
    setEditingJob(null);
    form.resetFields();
    setCronValue('0 0 0 * * *');
    form.setFieldsValue({
      name: '',
      description: '',
      analysis_period_days: 14,
      contacts_threshold: 10,
      recalculate_insights: false,
      contact_filter_entries: []
    });
    setModalVisible(true);
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setCronValue(job.cron_expression);
    const filterEntries = job.contact_filter
      ? Object.entries(job.contact_filter).map(([key, value]) => ({ key, value }))
      : [];
    form.setFieldsValue({
      name: job.name,
      description: job.description || '',
      analysis_period_days: job.analysis_period_days || 14,
      contacts_threshold: job.contacts_threshold || 10,
      recalculate_insights: job.recalculate_insights || false,
      contact_filter_entries: filterEntries
    });
    setModalVisible(true);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingJob(null);
    form.resetFields();
  };

  const handleModalSubmit = async (values) => {
    setLoading(true);
    try {
      const contactFilter = {};
      if (values.contact_filter_entries?.length) {
        for (const entry of values.contact_filter_entries) {
          if (entry?.key?.trim() && entry?.value?.trim()) {
            contactFilter[entry.key.trim()] = entry.value.trim();
          }
        }
      }

      const requestData = {
        name: values.name,
        description: values.description || null,
        cron_expression: cronValue,
        analysis_period_days: values.analysis_period_days,
        contacts_threshold: values.contacts_threshold,
        recalculate_insights: values.recalculate_insights || false,
        contact_filter: Object.keys(contactFilter).length > 0 ? contactFilter : null
      };

      if (editingJob) {
        await axios.put(`/analyze/scheduler/jobs/${editingJob.id}`, requestData);
        message.success('Scheduled job updated successfully');
      } else {
        await axios.post('/analyze/scheduler/jobs', requestData);
        message.success('Scheduled job created successfully');
      }

      setModalVisible(false);
      setEditingJob(null);
      form.resetFields();
      loadScheduledJobs();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save scheduled job';
      message.error(errorMsg);
      console.error('Error saving scheduled job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (job) => {
    try {
      await axios.post(`/analyze/scheduler/jobs/${job.id}/toggle`);
      message.success(`Job ${job.is_enabled ? 'disabled' : 'enabled'} successfully`);
      loadScheduledJobs();
    } catch (error) {
      message.error('Failed to toggle job status');
      console.error('Error toggling job status:', error);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`/analyze/scheduler/jobs/${jobId}`);
      message.success('Scheduled job deleted successfully');
      loadScheduledJobs();
    } catch (error) {
      message.error('Failed to delete scheduled job');
      console.error('Error deleting scheduled job:', error);
    }
  };

  const handleRunNow = async (job) => {
    try {
      const response = await axios.post(`/analyze/scheduler/jobs/${job.id}/run-now`);
      message.success('Job triggered successfully');
      // Navigate to results if desired
      if (response.data.triggered_job_id) {
        navigate(`/analysis?view=jobs`);
      }
    } catch (error) {
      message.error('Failed to trigger job');
      console.error('Error triggering job:', error);
    }
  };

  const viewLatestResults = (job) => {
    if (job.last_job_id) {
      navigate(`/analysis?jobId=${job.last_job_id}`);
    } else {
      message.info('No results available yet. This job has not run.');
    }
  };

  const getStatusTag = (job) => {
    if (!job.is_enabled) {
      return <Tag icon={<PauseCircleOutlined />} color="default">Paused</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />} color="green">Active</Tag>;
  };

  const columns = [
    {
      title: 'Scheduled Job',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockCircleOutlined style={{ color: record.is_enabled ? '#52c41a' : '#8c8c8c' }} />
            <span style={{ fontWeight: 'bold' }}>{text}</span>
            {getStatusTag(record)}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '22px' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Last Run',
      dataIndex: 'last_run_at',
      key: 'last_run_at',
      width: 150,
      render: (date, record) => (
        <div>
          {date ? (
            <>
              <div>{dayjs(date).format('YYYY-MM-DD HH:mm')}</div>
              <div style={{ fontSize: '11px', color: '#888' }}>
                Run #{record.run_count}
              </div>
            </>
          ) : (
            <Text type="secondary">Never</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Next Run',
      dataIndex: 'next_run_at',
      key: 'next_run_at',
      width: 150,
      render: (date, record) => (
        record.is_enabled && date ? (
          <div>
            <div>{dayjs(date).format('YYYY-MM-DD HH:mm')}</div>
            <div style={{ fontSize: '11px', color: '#888' }}>
              {dayjs(date).diff(dayjs(), 'hours')} hours from now
            </div>
          </div>
        ) : (
          <Text type="secondary">—</Text>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'toggle',
            label: record.is_enabled ? 'Disable' : 'Enable',
            icon: record.is_enabled ? <PauseCircleOutlined /> : <CheckCircleOutlined />,
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
          },
        ];

        const handleMenuClick = ({ key }) => {
          if (key === 'toggle') {
            handleToggleStatus(record);
          } else if (key === 'edit') {
            openEditModal(record);
          } else if (key === 'delete') {
            Modal.confirm({
              title: 'Delete Scheduled Job',
              content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
              okText: 'Delete',
              okType: 'danger',
              cancelText: 'Cancel',
              onOk: () => handleDelete(record.id),
            });
          }
        };

        return (
          <Space size="small">
            <Tooltip title="View Latest Results">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => viewLatestResults(record)}
                size="small"
                disabled={!record.last_job_id}
              />
            </Tooltip>
            <Tooltip title="Run Now">
              <Button
                type="link"
                icon={<PlayCircleOutlined />}
                onClick={() => handleRunNow(record)}
                size="small"
              />
            </Tooltip>
            <Dropdown
              menu={{ items: menuItems, onClick: handleMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button type="link" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="analysis-page">
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/analysis?view=jobs')}
              style={{ marginBottom: '16px' }}
            >
              Back to Jobs
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Title level={2} style={{ marginBottom: 0 }}>
                <SettingOutlined style={{ marginRight: '12px' }} />
                Job Scheduler Settings
              </Title>
              <Tag color="orange">Beta</Tag>
            </div>
            <Text type="secondary">
              Configure recurring analysis jobs. Scheduled jobs will run automatically based on their cron schedule.
            </Text>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadScheduledJobs}
              loading={tableLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              size="large"
            >
              New Scheduled Job
            </Button>
          </Space>
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={scheduledJobs}
          rowKey="id"
          loading={tableLoading}
          pagination={false}
          locale={{ emptyText: 'No scheduled jobs configured. Create one to automate your analysis runs.' }}
        />
      </Card>

      {/* Create/Edit Job Modal */}
      <Modal
        title={editingJob ? 'Edit Scheduled Job' : 'Create Scheduled Job'}
        open={modalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          <Form.Item
            name="name"
            label="Job Name"
            rules={[{ required: true, message: 'Please enter a job name' }]}
          >
            <Input placeholder="e.g., Weekly Analysis, Black Friday Campaign" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Optional description of this scheduled job"
              rows={2}
            />
          </Form.Item>

          <Form.Item
            label="Schedule (Cron Expression)"
            required
          >
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', background: '#fafafa' }}>
              <Cron
                value={cronValue}
                setValue={(val) => {
                    setCronValue(val);
                    console.log(val);
                }}
              />
            </div>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              Current expression: <code>{cronValue}</code>
            </Text>
          </Form.Item>

          <Form.Item
            name="analysis_period_days"
            label="Analysis Period (Days)"
            rules={[{ required: true, message: 'Please enter analysis period' }]}
          >
            <InputNumber min={1} max={90} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="contacts_threshold"
            label="Contacts Threshold"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={<span><FilterOutlined style={{ marginRight: 4 }} />Contact Filter</span>}>
            <Form.List name="contact_filter_entries">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Property key required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <PropertyKeyAutoComplete placeholder="Property key (e.g. company)" style={{ width: 220 }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        rules={[{ required: true, message: 'Match value required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Match value" style={{ width: 220 }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Filter
                  </Button>
                </>
              )}
            </Form.List>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
              Only analyze contacts whose properties match these values (substring match).
            </Text>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingJob ? 'Update' : 'Create'} Scheduled Job
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SchedulerSettingsPage;

